<script setup>
import { DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle } from 'reka-ui'
import { computed } from 'vue'
import { X } from 'lucide-vue-next'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  allowClose: {
    type: Boolean,
    default: true
  },
  closeOnOverlayClick: {
    type: Boolean,
    default: true
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg', 'xl', 'full'].includes(value)
  },
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'destructive'].includes(value)
  },
  title: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  },
  animation: {
    type: String,
    default: 'scale',
    validator: (value) => ['scale', 'slide', 'fade'].includes(value)
  },
  persistent: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:show', 'close', 'open'])

const handleOpenChange = (open) => {
  emit('update:show', open)
  if (open) {
    emit('open')
  } else {
    emit('close')
  }
}

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-full mx-4'
  }
  return sizes[props.size]
})

const variantClasses = computed(() => {
  const variants = {
    default: 'bg-white border-gray-200',
    destructive: 'bg-white border-danger-muted'
  }
  return variants[props.variant]
})

const animationClasses = computed(() => {
  const animations = {
    scale: [
      'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
      'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
      'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
    ].join(' '),
    slide: [
      'data-[state=closed]:slide-out-to-bottom-full data-[state=open]:slide-in-from-bottom-full'
    ].join(' '),
    fade: [
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
    ].join(' ')
  }
  return animations[props.animation]
})

const shouldPreventClose = computed(() => {
  return !props.allowClose || props.persistent || (!props.closeOnOverlayClick && props.allowClose)
})

const headerVariantClasses = computed(() => {
  return props.variant === 'destructive' 
    ? 'border-danger-light bg-danger-light/30' 
    : 'border-gray-100'
})

const titleVariantClasses = computed(() => {
  return props.variant === 'destructive' 
    ? 'text-danger' 
    : 'text-gray-900'
})
</script>

<template>
  <DialogRoot :open="show" @update:open="handleOpenChange">
    <DialogPortal>
      <DialogOverlay 
        class="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-200" 
      />
      <DialogContent 
        :class="[
          sizeClasses,
          variantClasses,
          animationClasses,
          'tb-modal-content w-full max-h-[90vh] rounded-base shadow-xl flex flex-col',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'duration-200 border'
        ]"
        :trap-focus="true"
        :disable-outside-pointer-events="shouldPreventClose"
        @pointer-down-outside="shouldPreventClose && $event.preventDefault()"
        @escape-key-down="(!allowClose || persistent) && $event.preventDefault()"
        role="dialog"
        :aria-labelledby="title ? 'modal-title' : undefined"
        :aria-describedby="description ? 'modal-description' : undefined"
      >
        <header 
          v-if="title || description || $slots.header || $slots.title || $slots.description" 
          :class="[
            'flex flex-col space-y-1.5 text-center sm:text-left px-6 py-4 border-b flex-shrink-0 relative',
            headerVariantClasses
          ]"
        >
          <DialogTitle 
            v-if="title || $slots.title || $slots.header" 
            :class="[
              'text-lg font-semibold leading-none tracking-tight',
              titleVariantClasses
            ]"
            id="modal-title"
          >
            <slot name="title">
              <slot name="header">{{ title }}</slot>
            </slot>
          </DialogTitle>
          <DialogDescription 
            v-if="description || $slots.description" 
            class="text-sm text-gray-600"
            id="modal-description"
          >
            <slot name="description">{{ description }}</slot>
          </DialogDescription>
          <button
            v-if="allowClose && !persistent"
            @click="handleOpenChange(false)"
            class="absolute right-4 top-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-base hover:bg-gray-50 transition-colors opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-muted"
            aria-label="Close dialog"
            type="button"
          >
            <X :size="16" />
          </button>
        </header>

        <div class="px-6 py-4 flex-1 min-h-0 overflow-y-auto">
          <slot></slot>
          <slot name="body"></slot>
        </div>

        <footer 
          v-if="$slots.footer || $slots.actions" 
          class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 px-6 py-4 border-t border-gray-100 flex-shrink-0 gap-2"
        >
          <slot name="actions">
            <slot name="footer"></slot>
          </slot>
        </footer>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>